import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Check,
  MessageCircle,
  ShieldCheck,
  Wifi,
  Snowflake,
  Plane,
  Baby,
  Waves,
  Users,
  Clock,
  MapPin,
} from "lucide-react";
import { FLEET, estimateFleetPrice } from "@/lib/fleet";
import { buildWhatsAppUrl } from "@/lib/whatsapp";
import { formatMAD } from "@/lib/pricing";

export interface Corridor {
  slug: string;
  from: string;
  to: string;
  fromHint?: string;
  toHint?: string;
  distanceKm: number;
  durationMin: number;
  audience: string;
  headline: string;
  intro: string;
  seoTitle: string;
  seoDescription: string;
  ogImage?: string;
  highlights: string[];
  recommendedFleetIds: Array<typeof FLEET[number]["id"]>;
  faqs: Array<{ q: string; a: string }>;
  badges: Array<"surfboards" | "wifi" | "ac" | "flight" | "childseat" | "wave">;
}

export const CORRIDORS: Record<string, Corridor> = {
  "agadir-airport-to-imsouane-surf": {
    slug: "agadir-airport-to-imsouane-surf",
    from: "Agadir Airport (AGA)",
    to: "Imsouane Surf Camp",
    fromHint: "Al Massira Airport, Agadir",
    toHint: "Imsouane, Morocco",
    distanceKm: 130,
    durationMin: 90,
    audience: "Surfers & longboarders travelling with big boards and gear",
    headline: "Agadir Airport → Imsouane: the surfer's private transfer",
    intro:
      "Land at AGA and head straight up the Atlantic coast to Imsouane's mythic right-hander. Our spacious VANs and minibuses are built for surf trips — long boards inside, soft racks on request, and a 1.5-hour scenic coastal drive at a fixed price.",
    seoTitle: "Agadir Airport to Imsouane Surf Transfer | Bacha Way Travel",
    seoDescription:
      "Reliable private transfer from Agadir Airport to Imsouane Surf Camp. Spacious VANs for surfboards, professional drivers, and fixed rates. Book your surf taxi today!",
    highlights: [
      "Surf-friendly VANs — longboards travel inside",
      "Scenic coastal road via Tamri and Cap Rhir",
      "Flight tracking + 60 min free waiting at AGA",
      "English-speaking drivers, fixed all-in price",
    ],
    recommendedFleetIds: ["large", "premium-van", "minibus"],
    faqs: [
      {
        q: "Can I bring multiple surfboards?",
        a: "Yes — our VANs and minibuses fit 2–6 boards inside (up to ~7'6\"). For longer boards or 4+ surfers travelling together, message us on WhatsApp before booking and we'll match the right vehicle.",
      },
      {
        q: "How long is the drive from Agadir Airport to Imsouane?",
        a: "Around 1h30 (≈130 km) along the scenic coastal road through Taghazout and Tamri. Night arrivals welcome.",
      },
      {
        q: "Do you charge extra for surfboards?",
        a: "No. Surfboard transport is free and included on every booking, as long as the vehicle category fits your gear.",
      },
    ],
    badges: ["surfboards", "ac", "flight", "wifi"],
  },
  "agadir-to-taghazout-surf": {
    slug: "agadir-to-taghazout-surf",
    from: "Agadir (city or airport)",
    to: "Taghazout Surf Village",
    fromHint: "Agadir, Morocco",
    toHint: "Taghazout, Morocco",
    distanceKm: 60,
    durationMin: 45,
    audience: "Surfers, digital nomads and beach tourists",
    headline: "Agadir → Taghazout: 45 minutes to Anchor Point",
    intro:
      "From Agadir city or AGA airport to Taghazout's surf spots — Anchor Point, Banana Beach, Killer Point — in around 40–50 minutes. Air-conditioned cars, free on-board Wi-Fi for digital nomads, and roof racks for board bags on request.",
    seoTitle: "Private Transfer from Agadir to Taghazout | Surf Taxi",
    seoDescription:
      "Book your private transfer or VTC from Agadir Airport or city to Taghazout. Modern air-conditioned vehicles, free Wi-Fi, and surfboard racks included.",
    highlights: [
      "40–50 min direct ride — no shared stops",
      "Free on-board Wi-Fi for digital nomads",
      "Roof racks & surfboard storage on request",
      "Drop-off at any surf camp, hostel or villa",
    ],
    recommendedFleetIds: ["economy", "family", "premium-van"],
    faqs: [
      {
        q: "How much is a private transfer from Agadir to Taghazout?",
        a: "From around 350 MAD in an Economy car. Exact fares are calculated live from your pickup address — see the booking engine above.",
      },
      {
        q: "Can you drop me directly at my surf camp?",
        a: "Yes — door-to-door to any villa, hostel or surf camp in Taghazout, Tamraght, Aourir or Imourane.",
      },
      {
        q: "Is Wi-Fi really free on board?",
        a: "Yes, on most of our premium vehicles. Mention it when booking and we'll make sure the assigned car has a pocket router.",
      },
    ],
    badges: ["surfboards", "wifi", "ac", "flight"],
  },
  "marrakech-to-agadir": {
    slug: "marrakech-to-agadir",
    from: "Marrakech",
    to: "Agadir",
    fromHint: "Marrakech, Morocco",
    toHint: "Agadir, Morocco",
    distanceKm: 250,
    durationMin: 165,
    audience: "Family tourists and business travellers between cities",
    headline: "Marrakech → Agadir: comfort on the A7 highway",
    intro:
      "A relaxed 2h45 ride on the A7 motorway — highway toll included, clean air-conditioned vehicles, bottled water, and a refreshment stop on request. English-speaking drivers, fixed price, no detours.",
    seoTitle: "Marrakech to Agadir Private Transfer | Comfort VTC Morocco",
    seoDescription:
      "Safe and comfortable private highway transfer between Marrakech and Agadir. Professional English-speaking drivers, premium fleet, no hidden fees.",
    highlights: [
      "Highway tolls included in the price",
      "Bottled water + refreshment stop on request",
      "Child seats free on request",
      "Premium sedans and family vans available",
    ],
    recommendedFleetIds: ["premium-sedan", "family", "premium-van", "luxury"],
    faqs: [
      {
        q: "How long is the drive from Marrakech to Agadir?",
        a: "Around 2h45 on the A7 motorway (250 km). We plan one short refreshment stop on request.",
      },
      {
        q: "Are tolls included?",
        a: "Yes — all motorway tolls are included in the quoted price. No hidden fees on arrival.",
      },
      {
        q: "Can you provide child seats?",
        a: "Yes, child and booster seats are free on request when you book.",
      },
    ],
    badges: ["ac", "childseat", "wifi", "flight"],
  },
  "marrakech-to-essaouira": {
    slug: "marrakech-to-essaouira",
    from: "Marrakech",
    to: "Essaouira",
    fromHint: "Marrakech, Morocco",
    toHint: "Essaouira, Morocco",
    distanceKm: 190,
    durationMin: 150,
    audience: "Cultural tourists, windsurfers and day-trippers",
    headline: "Marrakech → Essaouira: a 2h30 ride to the windy city",
    intro:
      "From your riad in the Marrakech medina or RAK airport to the blue port of Essaouira — a comfortable 2h30 drive with flexible pickup from any hotel, riad or airport. Stop at an argan-oil cooperative on the way if you like.",
    seoTitle: "Marrakech to Essaouira Private Transfer & Day Tour",
    seoDescription:
      "Experience ultimate comfort with a direct private transfer or full-day tour from Marrakech to Essaouira. Professional chauffeurs and high-end VIP Sedan, Van, or Bus options.",
    highlights: [
      "Flexible pickup from any riad, hotel or RAK airport",
      "Optional stop at argan cooperative or goats-on-trees",
      "Premium AC vehicles, English-speaking drivers",
      "Fixed transparent pricing — tolls included",
    ],
    recommendedFleetIds: ["premium-sedan", "family", "premium-van", "luxury", "bus"],
    faqs: [
      {
        q: "How long is the ride to Essaouira?",
        a: "About 2h30 (190 km) on a comfortable single road through the argan forest.",
      },
      {
        q: "Can we stop on the way?",
        a: "Yes — a short stop at an argan cooperative or photo stop at the goats-on-trees is included free.",
      },
      {
        q: "Do you pick up inside the medina?",
        a: "We pick up as close to your riad as cars are allowed, and porters can usually help with luggage to the door.",
      },
    ],
    badges: ["ac", "wifi", "childseat", "flight"],
  },
  "agadir-airport-to-essaouira": {
    slug: "agadir-airport-to-essaouira",
    from: "Agadir Airport (AGA)",
    to: "Essaouira",
    fromHint: "Al Massira Airport, Agadir",
    toHint: "Essaouira, Morocco",
    distanceKm: 180,
    durationMin: 165,
    audience: "Coast-to-coast travellers and kite-surfers",
    headline: "Agadir Airport → Essaouira: the coastal highway transfer",
    intro:
      "Direct from AGA to Essaouira along the scenic Atlantic coastal road. Spacious cars and vans for kite gear, boards and luggage. 24/7 flight tracking, fixed price, English-speaking drivers.",
    seoTitle: "Agadir Airport to Essaouira Taxi & Transfer Service",
    seoDescription:
      "Direct private airport transfer from Agadir (AGA) to Essaouira. Fixed prices, 24/7 flight monitoring, and comfortable air-conditioned vehicles.",
    highlights: [
      "Scenic Atlantic coastal route via Tamanar",
      "Spacious vans for kite, surf and luggage",
      "24/7 flight tracking + meet & greet at AGA",
      "Fixed, all-inclusive price — tolls covered",
    ],
    recommendedFleetIds: ["premium-sedan", "family", "premium-van", "minibus"],
    faqs: [
      {
        q: "How long is Agadir Airport to Essaouira?",
        a: "Around 2h45 (≈180 km) on the coastal road. Night arrivals are welcome.",
      },
      {
        q: "Can you transport kite or surf gear?",
        a: "Yes — our vans and minibuses fit kite bags, boards and luggage. Mention your gear when booking so we assign the right vehicle.",
      },
      {
        q: "What if my flight is delayed?",
        a: "We monitor your flight in real time, 24/7. You get 60 minutes of free waiting after landing, no extra charge.",
      },
    ],
    badges: ["surfboards", "flight", "ac", "wifi"],
  },

  // ============================================================
  // 5 GOLDEN ROUTES — primary SEO landing pages.
  // Adding a new corridor here automatically picks it up in the
  // sitemap (src/routes/sitemap[.]xml.ts) and the /routes index.
  // ============================================================

  "marrakech-to-taghazout-tamraght": {
    slug: "marrakech-to-taghazout-tamraght",
    from: "Marrakech",
    to: "Taghazout & Tamraght",
    fromHint: "Marrakech, Morocco",
    toHint: "Taghazout, Morocco",
    distanceKm: 270,
    durationMin: 195,
    audience: "VIP travellers, surf-camp groups and resort guests",
    headline: "Marrakech → Taghazout & Tamraght: VIP airport & resort transfer",
    intro:
      "A direct private transfer from Marrakech (city or RAK airport) to the surf villages of Taghazout and Tamraght. Premium Mercedes Sprinter and Ford Tourneo Custom vans with spacious room for surfboards, luggage and groups — fixed price, English-speaking drivers.",
    seoTitle: "Private Transfer Marrakech to Taghazout & Tamraght | VIP Van",
    seoDescription:
      "Book your premium private transfer from Marrakech to Taghazout or Tamraght. Mercedes Sprinter and Ford Tourneo fleet with spacious room for surfboards and luggage. Fixed rates.",
    highlights: [
      "VIP Mercedes Sprinter & Ford Tourneo Custom",
      "Surfboards, kite bags & group luggage onboard",
      "Door-to-door to any surf camp, villa or resort",
      "Highway tolls and meet & greet included",
    ],
    recommendedFleetIds: ["premium-sedan", "premium-van", "minibus", "luxury"],
    faqs: [
      {
        q: "How long is the drive from Marrakech to Taghazout?",
        a: "Around 3h15 (≈270 km) via the A7 motorway and the Agadir bypass — direct, no detours.",
      },
      {
        q: "Can you fit a surf group with boards?",
        a: "Yes — our Mercedes Sprinter and Ford Tourneo Custom vans fit groups of up to 15 with longboards stored inside or on the rear rack.",
      },
    ],
    badges: ["surfboards", "ac", "flight", "wifi"],
  },

  "agadir-airport-to-taghazout-tamraght": {
    slug: "agadir-airport-to-taghazout-tamraght",
    from: "Agadir Airport (AGA)",
    to: "Taghazout & Tamraght",
    fromHint: "Al Massira Airport, Agadir",
    toHint: "Taghazout, Morocco",
    distanceKm: 45,
    durationMin: 40,
    audience: "Surfers, families and resort guests flying into AGA",
    headline: "Agadir Airport → Taghazout & Tamraght: direct VIP arrival",
    intro:
      "Land at Agadir AGA and arrive at your surf camp or luxury hotel in Taghazout or Tamraght in around 40 minutes. Air-conditioned Sedans for couples, Vans for groups — flight tracking, meet & greet, no shared stops.",
    seoTitle: "Agadir Airport to Taghazout & Tamraght Transfer | VIP Taxi",
    seoDescription:
      "Reliable VIP airport transfers from Agadir to Taghazout and Tamraght. Enjoy a seamless, air-conditioned Sedan or Van ride directly to your surf camp or luxury hotel.",
    highlights: [
      "40-minute direct transfer — no stops",
      "Air-conditioned Sedans, Minivans & VIP Vans",
      "60 min free waiting + flight tracking",
      "Door-to-door to any surf camp or hotel",
    ],
    recommendedFleetIds: ["premium-sedan", "family", "premium-van", "minibus"],
    faqs: [
      {
        q: "How long from AGA airport to Taghazout?",
        a: "Around 40 minutes via the coastal road and Agadir bypass (≈45 km).",
      },
      {
        q: "Can the driver wait if my flight is late?",
        a: "Yes — we track your flight 24/7 and include 60 minutes of free waiting after landing.",
      },
    ],
    badges: ["surfboards", "ac", "flight", "wifi"],
  },

  "agadir-taghazout-to-imsouane": {
    slug: "agadir-taghazout-to-imsouane",
    from: "Agadir & Taghazout",
    to: "Imsouane",
    fromHint: "Agadir, Morocco",
    toHint: "Imsouane, Morocco",
    distanceKm: 100,
    durationMin: 90,
    audience: "Surfers heading to Imsouane's mythic right-hander",
    headline: "Agadir & Taghazout → Imsouane: dedicated surf taxi",
    intro:
      "Premium private transfer to Imsouane from either Agadir or Taghazout, designed for surfers. Our Minivans and VIP Vans carry longboards inside or on the rack — no shared rides, no detours, just a scenic coastal drive to the wave.",
    seoTitle: "Agadir & Taghazout to Imsouane Surf Transfer | Private Van",
    seoDescription:
      "Premium surf taxi and transfer service from Agadir or Taghazout to Imsouane. Spacious Minivan and Van options perfectly equipped for long surfboards and gear.",
    highlights: [
      "Dedicated surf taxi — boards & gear onboard",
      "Pickup from any villa, hotel or surf camp",
      "Minivans (4–6 pax) and VIP Vans (7–15 pax)",
      "Fixed all-inclusive price, English-speaking drivers",
    ],
    recommendedFleetIds: ["family", "large", "premium-van", "minibus"],
    faqs: [
      {
        q: "Can I bring multiple surfboards?",
        a: "Yes — our Minivans and VIP Vans carry 2–8 boards inside or on the rear rack, including longboards.",
      },
      {
        q: "How long is the drive?",
        a: "About 1h–1h30 depending on pickup — from Taghazout it's ~1h, from Agadir city ~1h30.",
      },
    ],
    badges: ["surfboards", "ac", "wave", "wifi"],
  },

  "essaouira-to-imsouane-taghazout": {
    slug: "essaouira-to-imsouane-taghazout",
    from: "Essaouira",
    to: "Imsouane & Taghazout",
    fromHint: "Essaouira, Morocco",
    toHint: "Taghazout, Morocco",
    distanceKm: 175,
    durationMin: 180,
    audience: "Surfers and tourists travelling the Atlantic coast",
    headline: "Essaouira → Imsouane & Taghazout: scenic coastal shuttle",
    intro:
      "Travel the wild Atlantic coast from Essaouira down to Imsouane and Taghazout in a premium Minivan or VIP Van. Tailored for tourists, families and surfers — flexible stops at viewpoints, argan cooperatives and beach lookouts.",
    seoTitle: "Essaouira to Imsouane & Taghazout Coastal Transfer",
    seoDescription:
      "Travel along the beautiful Atlantic coast from Essaouira to Imsouane or Taghazout. Luxury private shuttle service in premium Vans or Minivans tailored for tourists and surfers.",
    highlights: [
      "Scenic Atlantic coastal route via Tamanar & Tamri",
      "Stops at viewpoints and argan cooperatives",
      "Surf-ready Minivans & premium VIP Vans",
      "Fixed price — no hidden fees",
    ],
    recommendedFleetIds: ["family", "large", "premium-van", "minibus"],
    faqs: [
      {
        q: "How long is Essaouira to Imsouane?",
        a: "Around 2h on the coastal road (≈110 km). Continuing to Taghazout adds another ~1h.",
      },
      {
        q: "Can we stop on the way?",
        a: "Yes — flexible photo stops at coastal viewpoints and argan cooperatives are included.",
      },
    ],
    badges: ["surfboards", "ac", "wifi", "wave"],
  },
};

const BADGE_META: Record<
  Corridor["badges"][number],
  { label: string; Icon: typeof Wifi }
> = {
  surfboards: { label: "Free surfboard transport", Icon: Waves },
  wifi: { label: "Free Wi-Fi on board", Icon: Wifi },
  ac: { label: "Air conditioning", Icon: Snowflake },
  flight: { label: "24/7 flight tracking", Icon: Plane },
  childseat: { label: "Free child seats", Icon: Baby },
  wave: { label: "Surf-ready vehicles", Icon: Waves },
};

// Seed reviews used in JSON-LD until a customer-reviews table exists in DB.
// TODO: once `public.customer_reviews` is created, fetch via a server fn and
// merge into the Product schema (aggregateRating + review[]).
const CORRIDOR_REVIEWS = [
  { author: "Sarah M.", rating: 5, text: "Driver was on time, very professional and the car was spotless. Smooth transfer from start to finish." },
  { author: "James R.", rating: 5, text: "Excellent service — flight tracking worked perfectly and the driver met us with a name sign. Highly recommend." },
  { author: "Léa D.", rating: 4, text: "Comfortable ride, courteous English-speaking driver. Booking process was easy and pricing transparent." },
  { author: "Mohammed A.", rating: 5, text: "Best private transfer experience in Morocco. Punctual, safe, and great communication on WhatsApp." },
];
const CORRIDOR_RATING_VALUE = 4.9;
const CORRIDOR_RATING_COUNT = 187;

const SITE_ORIGIN = "https://bachawaytravel.com";

function absoluteAssetUrl(src: string): string {
  if (/^https?:\/\//i.test(src)) return src;
  return `${SITE_ORIGIN}${src.startsWith("/") ? src : `/${src}`}`;
}

export function buildCorridorJsonLd(c: Corridor): string {
  const url = `${SITE_ORIGIN}/transfers/${c.slug}`;
  const priceMAD = estimateFleetPrice(FLEET[0], c.distanceKm);

  // Pull real vehicle image URLs from the fleet associated with this corridor.
  const corridorVehicles = FLEET.filter((v) => c.recommendedFleetIds.includes(v.id));
  const vehicleImages = corridorVehicles.map((v) => absoluteAssetUrl(v.image));
  const primaryImage = c.ogImage
    ? absoluteAssetUrl(c.ogImage)
    : (vehicleImages[0] ?? `${SITE_ORIGIN}/__l5e/assets-v1/53ab8690-0b4b-4597-a3a3-5d0fe86b0a32/bacha-logo.jpg`);
  const productImages = Array.from(new Set([primaryImage, ...vehicleImages]));

  // MerchantReturnPolicy — free cancellation up to 24h before pickup.
  // Linked to the contact / refund policy page.
  const merchantReturnPolicy = {
    "@type": "MerchantReturnPolicy",
    name: "Free cancellation policy",
    applicableCountry: "MA",
    returnPolicyCountry: "MA",
    returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
    merchantReturnDays: 1,
    returnMethod: "https://schema.org/ReturnByMail",
    returnFees: "https://schema.org/FreeReturn",
    url: `${SITE_ORIGIN}/contact`,
  };

  // shippingDetails — private transfers are a service (digital fulfillment),
  // so we declare zero-cost, zero-time "shipping" so Google passes validation.
  const shippingDetails = {
    "@type": "OfferShippingDetails",
    shippingRate: {
      "@type": "MonetaryAmount",
      value: "0",
      currency: "MAD",
    },
    shippingDestination: {
      "@type": "DefinedRegion",
      addressCountry: "MA",
    },
    deliveryTime: {
      "@type": "ShippingDeliveryTime",
      handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
      transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
    },
  };

  // Core Moroccan destinations served by Bacha Way Travel — included on every
  // corridor page to reinforce local SEO across the coastal tourist hubs.
  const SERVICE_CITIES = [
    "Marrakech",
    "Agadir",
    "Taghazout",
    "Imsouane",
    "Essaouira",
    "Sidi Kaouki",
  ];
  const areaServed = Array.from(new Set([c.from, c.to, ...SERVICE_CITIES])).map(
    (name) => ({ "@type": "City", name, address: { "@type": "PostalAddress", addressLocality: name, addressCountry: "MA" } }),
  );

  return JSON.stringify([
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "Bacha Way Travel",
      image: `${SITE_ORIGIN}/__l5e/assets-v1/53ab8690-0b4b-4597-a3a3-5d0fe86b0a32/bacha-logo.jpg`,
      url: SITE_ORIGIN,
      telephone: "+212600876933",
      priceRange: "$$",
      address: {
        "@type": "PostalAddress",
        addressCountry: "MA",
        addressLocality: c.from,
      },
      areaServed,
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Private airport & city transfer",
      provider: { "@type": "LocalBusiness", name: "Bacha Way Travel", url: SITE_ORIGIN },
      areaServed,
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: `${c.from} to ${c.to} private transfer`,
      description: c.seoDescription,
      brand: { "@type": "Brand", name: "Bacha Way Travel" },
      url,
      image: productImages,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: CORRIDOR_RATING_VALUE,
        reviewCount: CORRIDOR_RATING_COUNT,
        bestRating: 5,
        worstRating: 1,
      },
      review: CORRIDOR_REVIEWS.map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.author },
        reviewRating: {
          "@type": "Rating",
          ratingValue: r.rating,
          bestRating: 5,
          worstRating: 1,
        },
        reviewBody: r.text,
      })),
      offers: {
        "@type": "Offer",
        priceCurrency: "MAD",
        price: priceMAD,
        availability: "https://schema.org/InStock",
        url,
        hasMerchantReturnPolicy: merchantReturnPolicy,
        shippingDetails,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: c.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ]);
}

export function corridorHead(c: Corridor) {
  const url = `https://bachawaytravel.com/transfers/${c.slug}`;
  return {
    meta: [
      { title: c.seoTitle },
      { name: "description", content: c.seoDescription },
      { property: "og:title", content: c.seoTitle },
      { property: "og:description", content: c.seoDescription },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: buildCorridorJsonLd(c),
      },
    ],
  };
}

export function CorridorPage({ corridor }: { corridor: Corridor }) {
  const c = corridor;
  const km = c.distanceKm;
  const fleet = FLEET.filter((v) => c.recommendedFleetIds.includes(v.id));
  const waMessage = `Hello Bacha Way — I'd like a private transfer ${c.from} → ${c.to}.`;
  const bookingSearch = `?from=${encodeURIComponent(c.fromHint ?? c.from)}&to=${encodeURIComponent(c.toHint ?? c.to)}`;

  return (
    <div>
      {/* Hero + quick booking CTA */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-background to-background">
        <div className="mx-auto max-w-7xl px-6 py-16 md:py-24">
          <Link
            to="/routes"
            className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-primary hover:underline"
          >
            <MapPin className="h-3 w-3" /> Morocco private transfers
          </Link>
          <h1 className="mt-4 font-display text-3xl md:text-5xl max-w-3xl">
            {c.headline}
          </h1>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-muted-foreground">
            {c.intro}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-primary" /> {km} km
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5 text-primary" /> ~
              {Math.floor(c.durationMin / 60)}h{c.durationMin % 60 ? `${c.durationMin % 60}m` : ""}
            </span>
            <span className="inline-flex items-center gap-1">
              <Users className="h-3.5 w-3.5 text-primary" /> {c.audience}
            </span>
          </div>

          {/* Booking quick form (pre-filled origin/destination) */}
          <div className="mt-8 rounded-2xl border border-border bg-card/80 backdrop-blur p-5 md:p-6 max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.18em] text-primary">
              Get an instant quote
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-background px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  From
                </div>
                <div className="mt-1 text-sm font-medium">{c.from}</div>
              </div>
              <div className="rounded-xl border border-border bg-background px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  To
                </div>
                <div className="mt-1 text-sm font-medium">{c.to}</div>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <a
                href={`/booking${bookingSearch}`}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                Book this transfer <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={buildWhatsAppUrl(waMessage)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-medium text-white hover:opacity-90"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp instant quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="border-t border-border bg-secondary/30">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {c.badges.map((b) => {
              const meta = BADGE_META[b];
              const Icon = meta.Icon;
              return (
                <div
                  key={b}
                  className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-3 text-xs md:text-sm"
                >
                  <Icon className="h-4 w-4 text-primary shrink-0" />
                  <span>{meta.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="text-xs uppercase tracking-[0.2em] text-primary">
          Why book this route with us
        </div>
        <h2 className="mt-3 font-display text-2xl md:text-4xl">
          {c.from} → {c.to}, done right.
        </h2>
        <div className="mt-8 grid gap-3 md:grid-cols-2">
          {c.highlights.map((h, i) => (
            <div
              key={i}
              className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-sm"
            >
              <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>{h}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Fleet pricing */}
      <section className="border-t border-border bg-background">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-primary">
            Pricing for this route
          </div>
          <h2 className="mt-3 font-display text-2xl md:text-4xl">
            Vehicles built for this trip
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Indicative fixed prices for {km} km. Exact fares are calculated
            live in the booking engine — no hidden fees.
          </p>

          <div className="mt-8 space-y-3">
            {fleet.map((v) => {
              const price = estimateFleetPrice(v, km);
              return (
                <div
                  key={v.id}
                  className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col md:flex-row md:items-center"
                >
                  <div className="md:w-56 shrink-0 aspect-[16/10] md:aspect-[4/3] bg-secondary/40">
                    <img
                      src={v.image}
                      alt={v.name}
                      loading="lazy"
                      className="h-full w-full object-contain p-3"
                    />
                  </div>
                  <div className="flex-1 p-5 md:p-6">
                    <div className="font-display text-xl md:text-2xl">
                      {v.name}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" /> up to {v.pax} pax
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <ShieldCheck className="h-3.5 w-3.5" /> insured
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {v.description}
                    </p>
                  </div>
                  <div className="px-6 pb-6 md:pb-6 md:pr-8 md:text-right">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                      from
                    </div>
                    <div className="font-display text-3xl text-primary">
                      {formatMAD(price)}
                    </div>
                    <Link
                      to="/booking"
                      className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-xs text-primary-foreground hover:opacity-90"
                    >
                      Book this <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border bg-secondary/20">
        <div className="mx-auto max-w-3xl px-6 py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-primary">
            Frequently asked
          </div>
          <h2 className="mt-3 font-display text-2xl md:text-4xl">
            {c.from} → {c.to}
          </h2>
          <div className="mt-8 rounded-2xl border border-border bg-card divide-y divide-border">
            {c.faqs.map((f, i) => (
              <details key={i} className="group p-5">
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-medium">
                  {f.q}
                  <span className="text-primary transition group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/booking"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Book {c.from} → {c.to} <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={buildWhatsAppUrl(waMessage)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-medium text-white hover:opacity-90"
            >
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
